/**
 * OffersPromoPage
 *
 * Offers & Promotions management page for Abraaj Water.
 * Supports inline cell editing, Excel import/export, delivery schedule
 * editing, and full Arabic ↔ English / RTL ↔ LTR switching via LanguageContext.
 *
 * Access: super_admin, entity_manager, or users with can_manage_offers=true
 * capability flag. Enforced via ProtectedRoute → canAccess('offers', user).
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';
import { useLanguage } from '../context/LanguageContext';
import api from '../utils/api';
import OffersPromoPageView from './OffersPromoPage.view';

// ─── Column keys used by the Excel import row builder ────────────────────────
const COLUMN_KEYS = [
  'sap_item_code', 'sap_bundle_code', 'product_name', 'product_name_ar',
  'product_family', 'size', 'pack_type', 'pack_qty_units', 'bundle_qty_packs',
  'original_price', 'promo_price', 'offer_type', 'free_items',
  'start_date', 'end_date', 'notes', 'status',
];

// ─── Empty row factory ────────────────────────────────────────────────────────
function emptyRow(id) {
  return {
    id,
    sap_item_code:    '',
    sap_bundle_code:  '',
    product_name:     '',
    product_name_ar:  '',
    product_family:   '',
    size:             '',
    pack_type:        '',
    pack_qty_units:   '',
    bundle_qty_packs: '',
    original_price:   '',
    promo_price:      '',
    offer_type:       '',
    free_items:       '',
    start_date:       '',
    end_date:         '',
    status:           'active',
    notes:            '',
  };
}

// ─── Flexible column picker for Excel import ──────────────────────────────────
// Returns the first non-empty raw value across candidate header names.
function pick(row, ...variants) {
  for (const v of variants) {
    const val = row[v];
    if (val !== undefined && val !== null && String(val).trim() !== '') {
      return val;
    }
  }
  return '';
}

// Trimmed string version of pick(). Preserves raw type only for date fields.
function pickStr(row, ...variants) {
  const v = pick(row, ...variants);
  return v === '' ? '' : String(v).trim();
}

// ─── Excel date parser ────────────────────────────────────────────────────────
// SheetJS (with raw:true) can return dates as:
//   1. A number  — Excel serial (days since Dec 30 1899, fractional part = time)
//   2. A string  — "2026-05-01", "2026-05-01 14:00", ISO with offset, etc.
//   3. A JS Date — safety net for cellDates:true callers
//   4. Empty / null / undefined
//
// Output is always "YYYY-MM-DDTHH:MM" — the format datetime-local inputs need.
function parseExcelDate(value) {
  if (value === null || value === undefined || value === '') return '';

  if (typeof value === 'number') {
    const MS_PER_DAY = 86400000;
    const excelEpoch = new Date(Date.UTC(1899, 11, 30));
    const ms = Math.round(value * MS_PER_DAY);
    const d  = new Date(excelEpoch.getTime() + ms);
    if (isNaN(d.getTime())) return '';
    return fmtDatetime(d);
  }

  if (value instanceof Date) {
    if (isNaN(value.getTime())) return '';
    return fmtDatetime(value);
  }

  if (typeof value === 'string') {
    const s = value.trim();
    if (!s) return '';
    const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(s);
    const withTime = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(s);
    if (dateOnly) return s + 'T00:00';
    if (withTime) return s.replace(' ', 'T').substring(0, 16);
    const d = new Date(s);
    if (!isNaN(d.getTime())) return fmtDatetime(d);
    return '';
  }
  return '';
}

function fmtDatetime(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return (
    d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate())
    + 'T' + pad(d.getHours()) + ':' + pad(d.getMinutes())
  );
}

// ─── Main container ──────────────────────────────────────────────────────────

export default function OffersPromoPage() {
  const { t, isRTL } = useLanguage();

  const [rows, setRows]                       = useState([]);
  const [filter, setFilter]                   = useState('all');
  const [search, setSearch]                   = useState('');
  const [delivery, setDelivery]               = useState(() => t('deliveryDefault'));
  const [editingDelivery, setEditingDelivery] = useState(false);
  const [deliveryDraft, setDeliveryDraft]     = useState('');
  const [dragging, setDragging]               = useState(false);
  const [syncing, setSyncing]                 = useState(false);
  // App perks editor state (persists in app_settings.app_perks)
  const [appPerks, setAppPerks]               = useState({ app_link: '', items: [] });
  const [appPerksSaving, setAppPerksSaving]   = useState(false);
  const [appPerksSavedAt, setAppPerksSavedAt] = useState(null);
  const fileInputRef                          = useRef(null);
  // Ref mirror of delivery for stale-closure-free reads inside async syncs.
  const deliveryRef = useRef(delivery);

  const statusLabels = {
    active:   t('statusActive'),
    expired:  t('statusExpired'),
    inactive: t('statusInactive'),
  };

  // ── Load persisted data on mount ─────────────────────────────────────────
  useEffect(() => {
    api.get('/offers/active')
      .then((r) => {
        if (r.data.offers?.length) {
          setRows(r.data.offers.map((o, i) => ({ ...emptyRow(Date.now() + i), ...o, id: Date.now() + i })));
        }
        if (r.data.delivery_schedule) {
          setDelivery(r.data.delivery_schedule);
          deliveryRef.current = r.data.delivery_schedule;
        }
      })
      .catch(() => { /* non-fatal — start with empty state */ });
    // Load app_perks for the editor
    api.get('/offers/app-perks')
      .then((r) => {
        setAppPerks({
          app_link: r.data.app_link || '',
          items: Array.isArray(r.data.items) ? r.data.items.map((i) => ({
            key: i.key || '', title: i.title || '', message: i.message || '',
            enabled: i.enabled !== false,
          })) : [],
        });
      })
      .catch(() => { /* non-fatal */ });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── App perks handlers ────────────────────────────────────────────────────
  const updateAppPerkLink = (v) => setAppPerks((p) => ({ ...p, app_link: v }));
  const updateAppPerkItem = (idx, field, value) => setAppPerks((p) => ({
    ...p, items: p.items.map((it, i) => i === idx ? { ...it, [field]: value } : it),
  }));
  const saveAppPerks = async () => {
    setAppPerksSaving(true);
    try {
      const res = await api.put('/offers/app-perks', appPerks);
      if (res.data?.saved) {
        setAppPerks({
          app_link: res.data.saved.app_link || '',
          items: (res.data.saved.items || []).map((i) => ({
            key: i.key || '', title: i.title || '', message: i.message || '',
            enabled: i.enabled !== false,
          })),
        });
      }
      setAppPerksSavedAt(new Date());
    } catch (e) {
      console.error('[app-perks] save failed:', e.message);
      alert('Save failed: ' + (e.response?.data?.error || e.message));
    } finally { setAppPerksSaving(false); }
  };

  // ── Sync to backend (debounced) ──────────────────────────────────────────
  const syncToBackend = useCallback((latestRows, latestDelivery) => {
    setSyncing(true);
    api.post('/offers/sync', {
      offers:            latestRows,
      delivery_schedule: latestDelivery ?? deliveryRef.current,
    })
      .catch((err) => console.error('[offers] sync failed:', err.message))
      .finally(() => setSyncing(false));
  }, []);

  // ── Derived stats ────────────────────────────────────────────────────────
  const totalOffers  = rows.length;
  const activeOffers = rows.filter((r) => r.status === 'active').length;
  const expiredCount = rows.filter((r) => r.status === 'expired').length;

  // Expiring Soon = active rows whose end_date falls in [now, now + 14d].
  // 14-day threshold per approved copy.
  const expiringSoonCount = (() => {
    const now = Date.now();
    const horizon = now + 14 * 86400000;
    return rows.filter((r) => {
      if (r.status !== 'active' || !r.end_date) return false;
      const t = Date.parse(r.end_date);
      return !isNaN(t) && t >= now && t <= horizon;
    }).length;
  })();

  // ── Filtered rows ────────────────────────────────────────────────────────
  const displayRows = rows.filter((row) => {
    let matchFilter = true;
    if (filter === 'active')       matchFilter = row.status === 'active';
    else if (filter === 'expired') matchFilter = row.status === 'expired';
    else if (filter === 'inactive') matchFilter = row.status === 'inactive';
    else if (filter === 'expiring_soon') {
      if (row.status !== 'active' || !row.end_date) matchFilter = false;
      else {
        const now = Date.now();
        const horizon = now + 14 * 86400000;
        const t = Date.parse(row.end_date);
        matchFilter = !isNaN(t) && t >= now && t <= horizon;
      }
    }

    const q = search.toLowerCase();
    const matchSearch = !q || COLUMN_KEYS.some((k) =>
      String(row[k] ?? '').toLowerCase().includes(q)
    );
    return matchFilter && matchSearch;
  });

  // ── Cell update (debounced sync) ─────────────────────────────────────────
  const syncTimer = useRef(null);
  const updateCell = useCallback((rowId, colKey, value) => {
    setRows((prev) => {
      const next = prev.map((r) => r.id === rowId ? { ...r, [colKey]: value } : r);
      clearTimeout(syncTimer.current);
      syncTimer.current = setTimeout(() => syncToBackend(next, deliveryRef.current), 800);
      return next;
    });
  }, [syncToBackend]);

  // ── Add row ──────────────────────────────────────────────────────────────
  const addRow = () => {
    setRows((prev) => {
      const next = [...prev, emptyRow(Date.now())];
      syncToBackend(next, deliveryRef.current);
      return next;
    });
  };

  // ── Delete row ───────────────────────────────────────────────────────────
  const deleteRow = (rowId) => {
    setRows((prev) => {
      const next = prev.filter((r) => r.id !== rowId);
      syncToBackend(next, deliveryRef.current);
      return next;
    });
    toast.success(t('toastDeleted'));
  };

  // ── Excel import ─────────────────────────────────────────────────────────
  const processFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const wb   = XLSX.read(data, { type: 'array', cellDates: false, raw: true });
        const ws   = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(ws, { defval: '' });

        if (!json.length) { toast.error(t('toastEmptyFile')); return; }

        const imported = json.map((row, i) => ({
          id: Date.now() + i,
          sap_item_code:    pickStr(row, 'SAP Item Code',        'sap_item_code',    'SAP Item',            'Item Code'),
          sap_bundle_code:  pickStr(row, 'SAP Bundle Code',      'sap_bundle_code',  'SAP Bundle',           'Bundle Code'),
          product_name:     pickStr(row, 'Product Name (EN)',    'Product Name',     'product_name',         'Name'),
          product_name_ar:  pickStr(row, 'Product Name (AR)',    'product_name_ar',  'Arabic Name',          'الاسم'),
          product_family:   pickStr(row, 'Product Family',       'product_family',   'Family',               'Product Category'),
          size:             pickStr(row, 'Size',                 'size',             'Product Size'),
          pack_type:        pickStr(row, 'Pack Type',            'pack_type',        'Pack Description',     'Pack'),
          pack_qty_units:   pickStr(row, 'Pack Qty (units)',     'Pack Qty',         'pack_qty_units',       'Units per Pack'),
          bundle_qty_packs: pickStr(row, 'Bundle Qty (packs)',   'Bundle Qty',       'bundle_qty_packs',     'Packs per Bundle'),
          free_items:       pickStr(row, 'Free Items',           'free_items',       'Free',                 'Bonus Items'),
          original_price:   pickStr(row, 'Original Price (KD)',  'Original Price',   'original_price',       'Price (KD)',       'Price'),
          promo_price:      pickStr(row, 'Promo Price (KD)',     'Promo Price',      'promo_price',          'Discounted Price', 'Discount Price', 'Sale Price'),
          offer_type:       pickStr(row, 'Offer Type',           'offer_type',       'Type',                 'Deal Type'),
          start_date:       parseExcelDate(pick(row, 'Start Date', 'start_date', 'Start Date & Time', 'Valid From', 'Offer Start')),
          end_date:         parseExcelDate(pick(row, 'End Date',   'end_date',   'End Date & Time',   'Valid To',   'Offer End',  'Expiry Date')),
          status:           (pickStr(row, 'Status', 'status') || 'active').toLowerCase(),
          notes:            pickStr(row, 'Notes',                'notes',            'Note',                 'Description',      'Remarks'),
        }));

        setRows((prev) => {
          // Deduplicate by sap_item_code: update existing rows, append new ones.
          const merged = [...prev];
          imported.forEach((imp) => {
            const key = (imp.sap_item_code || '').trim();
            if (key) {
              const idx = merged.findIndex((r) => (r.sap_item_code || '').trim() === key);
              if (idx !== -1) {
                merged[idx] = { ...imp, id: merged[idx].id };
                return;
              }
            }
            merged.push(imp);
          });
          syncToBackend(merged, deliveryRef.current);
          return merged;
        });
        toast.success(t('toastImported', { count: imported.length }));
      } catch {
        toast.error(t('toastReadError'));
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleFileChange = (e) => {
    processFile(e.target.files?.[0]);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    processFile(e.dataTransfer.files?.[0]);
  };

  const handleDragOver  = (e) => { e.preventDefault(); setDragging(true); };
  const handleDragLeave = ()  => setDragging(false);

  // ── Excel export ─────────────────────────────────────────────────────────
  const exportExcel = () => {
    if (!rows.length) { toast.error(t('toastNoData')); return; }
    const data = rows.map((r) => ({
      'SAP Item Code':       r.sap_item_code,
      'SAP Bundle Code':     r.sap_bundle_code,
      'Product Name (EN)':   r.product_name,
      'Product Name (AR)':   r.product_name_ar,
      'Product Family':      r.product_family,
      'Size':                r.size,
      'Pack Type':           r.pack_type,
      'Pack Qty (units)':    r.pack_qty_units,
      'Bundle Qty (packs)':  r.bundle_qty_packs,
      'Original Price (KD)': r.original_price,
      'Promo Price (KD)':    r.promo_price,
      'Offer Type':          r.offer_type,
      'Free Items':          r.free_items,
      'Start Date':          r.start_date,
      'End Date':            r.end_date,
      'Notes':               r.notes,
      'Status':              r.status,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Offers');
    XLSX.writeFile(wb, 'offers-export.xlsx');
    toast.success(t('toastExported'));
  };

  // ── Delivery edit ─────────────────────────────────────────────────────────
  const startEditDelivery = () => {
    setDeliveryDraft(delivery);
    setEditingDelivery(true);
  };
  const saveDelivery = () => {
    setDelivery(deliveryDraft);
    deliveryRef.current = deliveryDraft;
    setEditingDelivery(false);
    syncToBackend(rows, deliveryDraft);
  };

  // ── Filter chips ─────────────────────────────────────────────────────────
  const filters = [
    { key: 'all',            label: t('filterAll')      },
    { key: 'active',         label: t('filterActive')   },
    { key: 'expiring_soon',  label: 'Expiring soon'     },
    { key: 'expired',        label: t('filterExpired')  },
    { key: 'inactive',       label: t('filterInactive') },
  ];

  return (
    <OffersPromoPageView
      rows={rows}
      displayRows={displayRows}
      totalOffers={totalOffers}
      activeOffers={activeOffers}
      expiredCount={expiredCount}
      expiringSoonCount={expiringSoonCount}
      filters={filters}
      filter={filter}
      search={search}
      delivery={delivery}
      editingDelivery={editingDelivery}
      deliveryDraft={deliveryDraft}
      dragging={dragging}
      syncing={syncing}
      isRTL={isRTL}
      t={t}
      statusLabels={statusLabels}
      fileInputRef={fileInputRef}
      onAddRow={addRow}
      onExportExcel={exportExcel}
      onFileChange={handleFileChange}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onSearchChange={setSearch}
      onFilterChange={setFilter}
      onStartEditDelivery={startEditDelivery}
      onSaveDelivery={saveDelivery}
      onDeliveryDraftChange={setDeliveryDraft}
      onUpdateCell={updateCell}
      onDeleteRow={deleteRow}
      appPerks={appPerks}
      appPerksSaving={appPerksSaving}
      appPerksSavedAt={appPerksSavedAt}
      onAppPerkLinkChange={updateAppPerkLink}
      onAppPerkItemChange={updateAppPerkItem}
      onSaveAppPerks={saveAppPerks}
    />
  );
}
